import {AttrsSchema, Component} from 'panel';

import {aliasesResultFormatter, engageResultFormatter} from 'common/report/util/profile/formatters';
import {ProfileUserSummaryAttrs} from 'common/widgets/profile-user-summary';
import {FeatureGate, Permission} from 'common/report/constants';
import {currentProjectStore} from 'common/report/stores/project-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {identity} from 'common/util/function';
import {mapToSerializableObj} from 'common/util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {FormattedProperty, getRenderInputPropertyValue} from 'common/util/format-property';
import {HIDDEN_PEOPLE_PROPERTIES, MPProperties} from 'common/constants';
import {
  EngageQueryResult,
  FormattedAliasesResult,
  ProfileQueryEndpoint,
  ProfileQueryParameters,
} from 'common/report/util/profile/types';
import {extractUserSummaryProperties} from 'common/report/util/profile/utils';
import {profileClient} from 'common/report/queries/query-api/profile-client';
import {ProfileActivityAttrs} from 'common/widgets/profile-activity/types';
import {SelectEvent} from 'common/components/toggle/types';

import {getVisibleProperties} from '../profile-demographics/util/display-utils';
import {ProfileDemographicsAttrs} from '../profile-demographics';
import {template} from './template';
import './index.styl';
import {UserDetailTab} from './types';

export type LoadingStates = {
  aliases: boolean;
  dataGroup: boolean;
  profileProperties: boolean;
};

export type ProfileSidebarState = {
  aliases: string[];
  dataGroupID: string;
  selectedTab: UserDetailTab;
  dataGroup: {
    displayName: string;
    groupID: string;
    isReady: boolean;
    propertyName: string;
  };
  errored: Partial<LoadingStates>;
  loading: Partial<LoadingStates>;
  userProperties: Map<string, FormattedProperty>;
  profileExists: boolean;
};

export type ProfileSidebarJsxProps = {
  attrs: {
    'distinct-id'?: string;
    'data-group-id'?: string;
    'from-date'?: string;
    'to-date'?: string;
  };
};

export class ProfileSidebar extends Component<ProfileSidebarState, ProfileSidebarJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<ProfileSidebarJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'from-date': `string`,
      'to-date': `string`,
      'distinct-id': `string`,
      'data-group-id': `string`,
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        aliases: [],
        dataGroupID: null,
        dataGroup: null,
        selectedTab: UserDetailTab.Activities,
        errored: {
          aliases: false,
          profileProperties: false,
        },
        loading: {
          profileProperties: true,
          aliases: false,
        },
        userProperties: new Map(),
        profileExists: true,
      },
      helpers: {
        getUserSummaryAttrs: (): ProfileUserSummaryAttrs => {
          const distinctID = this.attr(`distinct-id`);
          const {
            aliases,
            userProperties,
            errored: {aliases: aliasesError},
          } = this.state;

          const userSummaryProps = extractUserSummaryProperties(this.state.userProperties, {
            dataGroupID: this.attr(`data-group-id`),
            dataGroup: this.state.dataGroup,
          });
          return {
            aliases: [distinctID].concat(aliases), // Since the distinctID can get clipped, ensure we put it first in the aliases
            'aliases-loading': this.state.loading.aliases,
            'aliases-error': aliasesError,
            'data-group-id': this.attr(`data-group-id`),
            'distinct-id': distinctID,
            'has-delete-permissions': [`owner`, `admin`].includes(currentProjectStore.value?.role.name),
            'hide-delete': true,
            'profile-exists': this.state.profileExists,
            'profile-summary': userSummaryProps,
            'project-id': parseInt(getProjectId()),
            'sidebar-mode': true,
            'user-properties': mapToSerializableObj(userProperties),
            'user-properties-loading': this.state.loading.profileProperties,
            'workspace-id': parseInt(getWorkspaceId()),
          };
        },
        getUserDemographicAttrs: (): ProfileDemographicsAttrs => {
          return {
            'distinct-id': this.attr(`distinct-id`),
            ...(this.attr(`data-group-id`) ? {'data-group-id': this.attr(`data-group-id`)} : {}),
            'data-group-name': this.state.dataGroup?.propertyName || null,
            'profile-exists': this.state.profileExists,
            'user-properties': getVisibleProperties(this.state.userProperties, HIDDEN_PEOPLE_PROPERTIES),
            'user-properties-error': this.state.errored.profileProperties,
            'user-properties-loading': this.state.loading.profileProperties,
            'utc-offset': currentProjectStore.value?.utcOffset,
            timezone: currentProjectStore.value?.timezone,
            'sidebar-mode': true,
            'can-write-profile': currentProjectStore.value?.hasPermission(Permission.WriteProfileExplore),
          };
        },
        getUserActivityAttrs: (): ProfileActivityAttrs => {
          return {
            'distinct-id': this.attr(`distinct-id`),
            'profile-email': getRenderInputPropertyValue(this.state.userProperties.get(MPProperties.Email)), // needed for tracking
            'history-limit-days': currentProjectStore.value?.getFeatureGate(FeatureGate.MaxDataHistoryDays),
            'sidebar-mode': true,
            'utc-offset': currentProjectStore.value?.utcOffset,
            timezone: currentProjectStore.value?.timezone,
            'project-as-of-date': currentProjectStore.value?.projectAsOfDate || ``,
            'from-date': this.attr(`from-date`),
            'to-date': this.attr(`to-date`),
            ...(this.attr(`data-group-id`) ? {'data-group-id': this.attr(`data-group-id`)} : {}),
          };
        },
      },
    };
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === `distinct-id` || name === `data-group-id`) {
      this.fetchData();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchData();
  }

  handleTabSelect = (ev: SelectEvent) => {
    this.dispatchEvent(
      new CustomEvent(`tabChange`, {
        detail: {
          selectedTab: ev.detail.selected,
        },
      }),
    );
    this.update({selectedTab: ev.detail.selected as UserDetailTab});
  };

  private fetchData() {
    if (!this.attr(`distinct-id`)) {
      return;
    }
    this.fetchAliases();
    this.fetchProfileProperties();
  }

  private async fetchProfileProperties(): Promise<void> {
    this.update({
      loading: {
        ...this.state.loading,
        profileProperties: true,
      },
    });
    const distinctID = this.attr(`distinct-id`);
    const projectId = getProjectId();
    const workspaceId = getWorkspaceId();
    const dataGroupID = this.attr(`data-group-id`);

    const queryParams = {
      dataGroupID,
      distinctID,
      projectId,
      workspaceId,
    };

    const res = await this.queryEndpoint<EngageQueryResult>(ProfileQueryEndpoint.Engage, queryParams, false);
    const {results, profileExists, error} = engageResultFormatter(
      res,
      dataGroupID,
      currentProjectStore.value?.timezone,
    );

    this.update({
      errored: {
        ...this.state.errored,
        profileProperties: error,
      },
      loading: {
        ...this.state.loading,
        profileProperties: false,
      },
      userProperties: results || new Map(),
      profileExists,
    });
  }

  private async fetchAliases(): Promise<void> {
    this.update({
      loading: {
        ...this.state.loading,
        aliases: true,
      },
    });
    const distinctID = this.attr(`distinct-id`);
    const projectId = getProjectId();
    const workspaceId = getWorkspaceId();

    const queryParams = {
      distinctID,
      projectId,
      workspaceId,
    };

    const {aliases} = await this.queryEndpoint<FormattedAliasesResult>(
      ProfileQueryEndpoint.Aliases,
      queryParams,
      false,
      aliasesResultFormatter,
    );

    this.update({
      aliases,
      loading: {
        ...this.state.loading,
        aliases: false,
      },
    });
  }

  private queryEndpoint<ResultT>(
    endpoint: ProfileQueryEndpoint,
    params: ProfileQueryParameters,
    clearCache = false,
    resultsFormatter: (results: any) => ResultT = identity,
  ): Promise<ResultT> {
    return profileClient
      .fetchProfileEndpoint<any>(params, endpoint, clearCache)
      .then((results) => resultsFormatter(results));
  }
}

export const ProfileSidebarEl = defineAndMakeJsxFactory<ProfileSidebarJsxProps>(`profile-sidebar`, ProfileSidebar);
export const ProfileSidebarReact = panel2react<ProfileSidebarJsxProps>(`profile-sidebar`, ProfileSidebar);
